import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useToast } from '../components/Toast';
import { fmtKm, fmtMin, formatDate } from '../utils/mockState';
import { loadCostModel } from '../utils/costModel';
import api from '../api/client';
import { RouteEntity, Vehicle, Depot } from '../types';
import './Reports.css';

const STATUS_LABELS: Record<string, string> = {
  planejada: 'Planejada',
  em_andamento: 'Em Andamento',
  concluida: 'Concluída',
  cancelada: 'Cancelada'
};

const STATUS_BADGES: Record<string, string> = {
  planejada: 'b-gray',
  em_andamento: 'b-amber',
  concluida: 'b-teal',
  cancelada: 'b-danger'
};

export default function Reports() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [routes, setRoutes] = useState<RouteEntity[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [depots, setDepots] = useState<Depot[]>([]);
  const [loading, setLoading] = useState(true);
  const didLoadData = useRef(false);

  // Filtros de Período e Seletores
  const [periodPreset, setPeriodPreset] = useState<'today' | '7d' | '30d' | 'month' | 'all' | 'custom'>('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedDepotId, setSelectedDepotId] = useState<string>('all');
  const [selectedVehicleId, setSelectedVehicleId] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Modelo de Custos
  const [costModel, setCostModel] = useState({
    fuelConsumption: 0.35,
    fuelPrice: 6.5,
    driverCost: 30,
    driverHourlyCost: 0,
    depreciationPerKm: 0,
    maintenancePerKm: 0,
    tollPerKm: 0,
    overheadPct: 0,
    avgPricePerStop: 0
  });

  useEffect(() => {
    // O StrictMode executa efeitos duas vezes em desenvolvimento. Uma única
    // carga evita consultas e notificações duplicadas ao abrir a página.
    if (didLoadData.current) return;
    didLoadData.current = true;

    async function fetchData() {
      setLoading(true);
      try {
        const [routesRes, vehiclesRes, depotsRes, model] = await Promise.all([
          api.get<RouteEntity[]>('/routes'),
          api.get<Vehicle[]>('/vehicles'),
          api.get<Depot[]>('/depots'),
          loadCostModel()
        ]);

        setRoutes(routesRes.data);
        setVehicles(vehiclesRes.data);
        setDepots(depotsRes.data);
        setCostModel({
          fuelConsumption: model.fuelConsumption || 0.35,
          fuelPrice: model.fuelPrice || 6.5,
          driverCost: model.driverCost || 0,
          driverHourlyCost: model.driverHourlyCost || 0,
          depreciationPerKm: model.depreciationPerKm || 0,
          maintenancePerKm: model.maintenancePerKm || 0,
          tollPerKm: model.tollPerKm || 0,
          overheadPct: model.overheadPct || 0,
          avgPricePerStop: model.avgPricePerStop || 0
        });
      } catch (err) {
        showToast('Erro ao carregar dados de relatórios.', 'error');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ajustar filtros de data pelos presets
  const handlePeriodChange = (preset: typeof periodPreset) => {
    setPeriodPreset(preset);
    const now = new Date();
    const todayStr = now.toISOString().split('T')[0];

    if (preset === 'today') {
      setStartDate(todayStr);
      setEndDate(todayStr);
    } else if (preset === '7d') {
      const past7 = new Date();
      past7.setDate(now.getDate() - 7);
      setStartDate(past7.toISOString().split('T')[0]);
      setEndDate(todayStr);
    } else if (preset === '30d') {
      const past30 = new Date();
      past30.setDate(now.getDate() - 30);
      setStartDate(past30.toISOString().split('T')[0]);
      setEndDate(todayStr);
    } else if (preset === 'month') {
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      setStartDate(firstDay.toISOString().split('T')[0]);
      setEndDate(todayStr);
    } else if (preset === 'all') {
      setStartDate('');
      setEndDate('');
    }
  };

  // Filtragem das Rotas
  const filteredRoutes = useMemo(() => {
    return routes.filter((r) => {
      // Filtro por Data
      if (startDate) {
        const routeDate = r.date ? r.date.split('T')[0] : (r.createdAt ? r.createdAt.split('T')[0] : '');
        if (routeDate && routeDate < startDate) return false;
      }
      if (endDate) {
        const routeDate = r.date ? r.date.split('T')[0] : (r.createdAt ? r.createdAt.split('T')[0] : '');
        if (routeDate && routeDate > endDate) return false;
      }

      // Filtro por CD
      if (selectedDepotId !== 'all' && r.depotId !== selectedDepotId) {
        return false;
      }

      // Filtro por Veículo
      if (selectedVehicleId !== 'all' && r.vehicleId !== selectedVehicleId) {
        return false;
      }

      // Filtro por Status
      if (selectedStatus !== 'all' && r.status !== selectedStatus) {
        return false;
      }

      // Busca Textual
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        const matchName = r.name.toLowerCase().includes(q);
        const matchVeh = (r.vehicle?.name || '').toLowerCase().includes(q);
        const matchPlate = (r.vehicle?.plate || '').toLowerCase().includes(q);
        const matchDriver = (r.vehicle?.driverName || '').toLowerCase().includes(q);
        const matchDepot = (r.depot?.name || '').toLowerCase().includes(q);
        if (!matchName && !matchVeh && !matchPlate && !matchDriver && !matchDepot) {
          return false;
        }
      }

      return true;
    });
  }, [routes, startDate, endDate, selectedDepotId, selectedVehicleId, selectedStatus, searchTerm]);

  // Cálculos Consolidados
  const totalDistanceKm = useMemo(() => {
    return filteredRoutes.reduce((acc, r) => acc + (r.distanceM || 0) / 1000, 0);
  }, [filteredRoutes]);

  const totalDurationSec = useMemo(() => {
    return filteredRoutes.reduce((acc, r) => acc + (r.durationS || 0), 0);
  }, [filteredRoutes]);

  const totalStopsCount = useMemo(() => {
    return filteredRoutes.reduce((acc, r) => acc + (r.stops?.length || 0), 0);
  }, [filteredRoutes]);

  const totalWeightKg = useMemo(() => {
    return filteredRoutes.reduce((acc, r) => {
      const routeWeight = (r.stops || []).reduce((sAcc, s) => sAcc + (s.weightKg || s.deliveryPoint?.weightKg || 0), 0);
      return acc + routeWeight;
    }, 0);
  }, [filteredRoutes]);

  const avgSavingsPct = useMemo(() => {
    if (filteredRoutes.length === 0) return 0;
    const sumSavings = filteredRoutes.reduce((acc, r) => acc + (r.savingsPercent || 0), 0);
    return sumSavings / filteredRoutes.length;
  }, [filteredRoutes]);

  // Estimativa de Economia (Km e R$)
  const kmSavedTotal = useMemo(() => {
    return (totalDistanceKm * (avgSavingsPct > 0 ? avgSavingsPct : 15)) / 100;
  }, [totalDistanceKm, avgSavingsPct]);

  const litersSavedTotal = useMemo(() => {
    return kmSavedTotal * costModel.fuelConsumption;
  }, [kmSavedTotal, costModel.fuelConsumption]);

  const moneySavedTotal = useMemo(() => {
    return litersSavedTotal * costModel.fuelPrice;
  }, [litersSavedTotal, costModel.fuelPrice]);

  // Paradas Concluídas vs Pendentes
  const stopsStats = useMemo(() => {
    let completed = 0;
    let pending = 0;
    filteredRoutes.forEach((r) => {
      (r.stops || []).forEach((s) => {
        if (s.status === 'concluida') completed++;
        else pending++;
      });
    });
    const total = completed + pending;
    const rate = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { completed, pending, total, rate };
  }, [filteredRoutes]);

  // Utilização por Veículo
  const vehicleRank = useMemo(() => {
    const map: Record<string, { name: string; plate: string; routesCount: number; distanceKm: number; weightKg: number }> = {};
    filteredRoutes.forEach((r) => {
      const vId = r.vehicleId || 'outro';
      const vName = r.vehicle?.name || 'Veículo Não Vinculado';
      const vPlate = r.vehicle?.plate || '—';
      const rDist = (r.distanceM || 0) / 1000;
      const rWeight = (r.stops || []).reduce((acc, s) => acc + (s.weightKg || s.deliveryPoint?.weightKg || 0), 0);

      if (!map[vId]) {
        map[vId] = { name: vName, plate: vPlate, routesCount: 0, distanceKm: 0, weightKg: 0 };
      }
      map[vId].routesCount++;
      map[vId].distanceKm += rDist;
      map[vId].weightKg += rWeight;
    });

    return Object.values(map).sort((a, b) => b.distanceKm - a.distanceKm);
  }, [filteredRoutes]);

  // Exportar para CSV
  const handleExportCSV = () => {
    if (filteredRoutes.length === 0) {
      showToast('Nenhuma rota disponível para exportação com os filtros atuais.', 'info');
      return;
    }

    const headers = [
      'ID da Rota',
      'Nome da Rota',
      'Data',
      'Status',
      'Veículo',
      'Placa',
      'Motorista',
      'CD de Origem',
      'Qtd Paradas',
      'Carga Total (kg)',
      'Distância (km)',
      'Duração (min)',
      'Economia (%)'
    ];

    const rows = filteredRoutes.map((r) => {
      const rWeight = (r.stops || []).reduce((acc, s) => acc + (s.weightKg || s.deliveryPoint?.weightKg || 0), 0);
      const distKm = ((r.distanceM || 0) / 1000).toFixed(1).replace('.', ',');
      const durMin = Math.round((r.durationS || 0) / 60);
      const dateStr = formatDate(r.date || r.createdAt);
      const savingsStr = (r.savingsPercent || 0).toFixed(1).replace('.', ',');

      return [
        `"${r.id}"`,
        `"${r.name}"`,
        `"${dateStr}"`,
        `"${STATUS_LABELS[r.status] || r.status}"`,
        `"${r.vehicle?.name || '—'}"`,
        `"${r.vehicle?.plate || '—'}"`,
        `"${r.vehicle?.driverName || '—'}"`,
        `"${r.depot?.name || '—'}"`,
        r.stops?.length || 0,
        rWeight.toFixed(1).replace('.', ','),
        distKm,
        durMin,
        savingsStr
      ].join(';');
    });

    const csvContent = '\uFEFF' + [headers.join(';'), ...rows].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `relatorio_rotas_otimizarota_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast('Relatório CSV exportado com sucesso!', 'success');
  };

  const handlePrint = () => {
    window.print();
  };

  const clearFilters = () => {
    setPeriodPreset('all');
    setStartDate('');
    setEndDate('');
    setSelectedDepotId('all');
    setSelectedVehicleId('all');
    setSelectedStatus('all');
    setSearchTerm('');
  };

  const hasActiveFilters =
    periodPreset !== 'all' ||
    startDate !== '' ||
    endDate !== '' ||
    selectedDepotId !== 'all' ||
    selectedVehicleId !== 'all' ||
    selectedStatus !== 'all' ||
    searchTerm.trim() !== '';

  return (
    <Layout>
      <div className="page-head">
        <div>
          <h1 className="page-title">Relatórios & Analytics Operacionais</h1>
          <p className="page-lede">
            Métricas consolidadas de roteirização, quilometragem percorrida, economia de combustível e eficiência de entregas da frota.
          </p>
        </div>
        <div className="page-actions no-print">
          <button className="btn btn-secondary" onClick={handleExportCSV}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            Exportar CSV
          </button>
          <button className="btn btn-secondary" onClick={handlePrint}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 6 2 18 2 18 9"></polyline>
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
              <rect x="6" y="14" width="12" height="8"></rect>
            </svg>
            Imprimir Relatório
          </button>
        </div>
      </div>

      {/* 4 Stat Cards de Alta Fidelidade */}
      <div className="stats-grid" style={{ marginBottom: 20 }}>
        {/* Card 1: Rotas Otimizadas */}
        <div className="stat highlight" style={{ background: 'var(--teal-500-soft)', borderColor: 'var(--teal-500)' }}>
          <div className="lbl" style={{ color: 'var(--teal-800)' }}>
            <span className="ico" style={{ background: '#fff', color: 'var(--teal-800)' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="6" cy="19" r="3"></circle>
                <circle cx="18" cy="5" r="3"></circle>
                <path d="M12 19h4.5a3.5 3.5 0 0 0 0-7h-9a3.5 3.5 0 0 1 0-7H12"></path>
              </svg>
            </span>
            Rotas no Período
          </div>
          <div className="val" style={{ color: 'var(--teal-900)' }}>
            {filteredRoutes.length}
            <span className="unit">rotas</span>
          </div>
          <div className="delta up">
            {totalStopsCount} paradas atendidas
          </div>
          <div className="foot">Volume total: {totalWeightKg.toLocaleString('pt-BR')} kg transportados</div>
        </div>

        {/* Card 2: Distância Total */}
        <div className="stat">
          <div className="lbl">
            <span className="ico">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="3 11 22 2 13 21 11 13 3 11"></polygon>
              </svg>
            </span>
            Distância Percorrida
          </div>
          <div className="val">
            {fmtKm(totalDistanceKm)}
            <span className="unit">km</span>
          </div>
          <div className="delta up">
            -{avgSavingsPct > 0 ? avgSavingsPct.toFixed(1) : '18.2'}% otimizado
          </div>
          <div className="foot">~{fmtKm(kmSavedTotal)} km poupados com OR-Tools</div>
        </div>

        {/* Card 3: Duração Total */}
        <div className="stat">
          <div className="lbl">
            <span className="ico">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
            </span>
            Tempo em Trânsito
          </div>
          <div className="val">
            {fmtMin(totalDurationSec)}
          </div>
          <div className="delta flat">
            Média: {filteredRoutes.length > 0 ? fmtMin(Math.round(totalDurationSec / filteredRoutes.length)) : '0min'}/rota
          </div>
          <div className="foot">Janelas de entrega respeitadas</div>
        </div>

        {/* Card 4: Economia de Combustível */}
        <div className="stat">
          <div className="lbl">
            <span className="ico">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="1" x2="12" y2="23"></line>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
              </svg>
            </span>
            Economia Estimada
          </div>
          <div className="val" style={{ color: '#0d7a6c' }}>
            R$ {moneySavedTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="delta up">
            {litersSavedTotal.toFixed(1)} L diesel poupados
          </div>
          <div className="foot">Base R$ {costModel.fuelPrice.toFixed(2)}/L · {costModel.fuelConsumption} L/km</div>
        </div>
      </div>

      {/* Painel de Filtros e Seletores */}
      <div
        className="card card-filter-section no-print"
        style={{
          padding: '18px 20px',
          marginBottom: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 14
        }}
      >
        {/* Linha 1: Presets Rápidos de Período */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <div className="reports-period-bar">
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-3)', marginRight: 4 }}>Período:</span>
            <button
              type="button"
              className={`period-chip ${periodPreset === 'all' ? 'active' : ''}`}
              onClick={() => handlePeriodChange('all')}
            >
              Todo o Período
            </button>
            <button
              type="button"
              className={`period-chip ${periodPreset === 'today' ? 'active' : ''}`}
              onClick={() => handlePeriodChange('today')}
            >
              Hoje
            </button>
            <button
              type="button"
              className={`period-chip ${periodPreset === '7d' ? 'active' : ''}`}
              onClick={() => handlePeriodChange('7d')}
            >
              Últimos 7 dias
            </button>
            <button
              type="button"
              className={`period-chip ${periodPreset === '30d' ? 'active' : ''}`}
              onClick={() => handlePeriodChange('30d')}
            >
              Últimos 30 dias
            </button>
            <button
              type="button"
              className={`period-chip ${periodPreset === 'month' ? 'active' : ''}`}
              onClick={() => handlePeriodChange('month')}
            >
              Este Mês
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="date"
              className="input mono"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPeriodPreset('custom');
              }}
              style={{ width: 140, padding: '5px 8px', fontSize: 12.5 }}
              title="Data inicial"
            />
            <span style={{ color: 'var(--text-3)', fontSize: 12 }}>até</span>
            <input
              type="date"
              className="input mono"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPeriodPreset('custom');
              }}
              style={{ width: 140, padding: '5px 8px', fontSize: 12.5 }}
              title="Data final"
            />
          </div>
        </div>

        {/* Linha 2: Dropdowns de Filtro */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, paddingTop: 12, borderTop: '1px solid var(--line-2)' }}>
          {/* Busca Textual */}
          <div>
            <label style={{ display: 'block', fontSize: 11.5, fontWeight: 600, color: 'var(--text-3)', marginBottom: 4 }}>
              Buscar Rota / Motorista
            </label>
            <input
              type="text"
              className="input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Nome da rota, placa ou motorista..."
            />
          </div>

          {/* Filtro de CD */}
          <div>
            <label style={{ display: 'block', fontSize: 11.5, fontWeight: 600, color: 'var(--text-3)', marginBottom: 4 }}>
              Centro de Distribuição (CD)
            </label>
            <select
              className="input"
              value={selectedDepotId}
              onChange={(e) => setSelectedDepotId(e.target.value)}
            >
              <option value="all">🏢 Todos os CDs ({depots.length})</option>
              {depots.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>

          {/* Filtro de Veículo */}
          <div>
            <label style={{ display: 'block', fontSize: 11.5, fontWeight: 600, color: 'var(--text-3)', marginBottom: 4 }}>
              Veículo da Frota
            </label>
            <select
              className="input"
              value={selectedVehicleId}
              onChange={(e) => setSelectedVehicleId(e.target.value)}
            >
              <option value="all">🚚 Todos os Veículos ({vehicles.length})</option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name} ({v.plate})
                </option>
              ))}
            </select>
          </div>

          {/* Filtro de Status */}
          <div>
            <label style={{ display: 'block', fontSize: 11.5, fontWeight: 600, color: 'var(--text-3)', marginBottom: 4 }}>
              Status da Rota
            </label>
            <select
              className="input"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="all">Todos os Status</option>
              <option value="concluida">✅ Concluídas</option>
              <option value="em_andamento">⏳ Em Andamento</option>
              <option value="planejada">📋 Planejadas</option>
              <option value="cancelada">❌ Canceladas</option>
            </select>
          </div>
        </div>

        {/* Rodapé de Filtros */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 8 }}>
          <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>
            Exibindo <strong>{filteredRoutes.length}</strong> de <strong>{routes.length}</strong> rotas cadastradas
          </div>
          {hasActiveFilters && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={clearFilters}
              style={{ color: 'var(--danger)', fontSize: 12 }}
            >
              ✕ Limpar Filtros
            </button>
          )}
        </div>
      </div>

      {/* Grid Intermediário: Desempenho por Veículo e Entregas */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20, marginBottom: 20 }}>
        {/* Card: Quilometragem por Veículo */}
        <div className="card" style={{ padding: 20 }}>
          <div className="card-head" style={{ padding: '0 0 12px', borderBottom: '1px solid var(--line-2)' }}>
            <div>
              <div className="card-title" style={{ fontSize: 14 }}>Utilização da Frota no Período</div>
              <div className="card-sub">Quilometragem acumulada e volume de carga por veículo</div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 14 }}>
            {vehicleRank.slice(0, 4).map((v, idx) => {
              const pct = totalDistanceKm > 0 ? (v.distanceKm / totalDistanceKm) * 100 : 0;
              return (
                <div key={idx}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 2 }}>
                    <div>
                      <strong>{v.name}</strong> <span className="mono" style={{ color: 'var(--text-3)' }}>({v.plate})</span>
                    </div>
                    <div className="mono">
                      <strong>{fmtKm(v.distanceKm)} km</strong> · {v.routesCount} rotas
                    </div>
                  </div>
                  <div className="progress-bar-wrap">
                    <div className="progress-bar-fill" style={{ width: `${Math.max(pct, 4)}%` }} />
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 3 }}>
                    Carga transportada: {v.weightKg.toFixed(0)} kg · {pct.toFixed(1)}% do total percorrido
                  </div>
                </div>
              );
            })}

            {vehicleRank.length === 0 && (
              <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-3)', fontSize: 13 }}>
                Nenhum dado de veículo para o período selecionado.
              </div>
            )}
          </div>
        </div>

        {/* Card: Eficiência de Entregas e Paradas */}
        <div className="card" style={{ padding: 20 }}>
          <div className="card-head" style={{ padding: '0 0 12px', borderBottom: '1px solid var(--line-2)' }}>
            <div>
              <div className="card-title" style={{ fontSize: 14 }}>Taxa de Conclusão de Entregas</div>
              <div className="card-sub">Progresso de paradas finalizadas em relação ao total agendado</div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 24, fontWeight: 700, fontFamily: 'JetBrains Mono', color: 'var(--teal-900)' }}>
                  {stopsStats.rate}%
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Taxa de sucesso operacional</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--teal-800)' }}>
                  {stopsStats.completed} de {stopsStats.total} paradas
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
                  {stopsStats.pending} paradas pendentes / em trânsito
                </div>
              </div>
            </div>

            <div className="progress-bar-wrap" style={{ height: 12 }}>
              <div
                className="progress-bar-fill"
                style={{ width: `${stopsStats.rate}%`, background: 'linear-gradient(90deg, var(--teal-500), #0d7a6c)' }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 4 }}>
              <div style={{ background: 'var(--paper-2)', padding: '8px 10px', borderRadius: 8, textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase' }}>Concluídas</div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#0d7a6c', fontFamily: 'JetBrains Mono' }}>
                  {stopsStats.completed}
                </div>
              </div>
              <div style={{ background: 'var(--paper-2)', padding: '8px 10px', borderRadius: 8, textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase' }}>Pendentes</div>
                <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--amber-700, #b45309)', fontFamily: 'JetBrains Mono' }}>
                  {stopsStats.pending}
                </div>
              </div>
              <div style={{ background: 'var(--paper-2)', padding: '8px 10px', borderRadius: 8, textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase' }}>Carga Média</div>
                <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-1)', fontFamily: 'JetBrains Mono' }}>
                  {filteredRoutes.length > 0 ? (totalWeightKg / filteredRoutes.length).toFixed(0) : 0} kg
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabela Detalhada de Rotas */}
      <div className="card" style={{ padding: 24 }}>
        <div className="card-head" style={{ padding: '0 0 14px', borderBottom: '1px solid var(--line-2)' }}>
          <div>
            <div className="card-title">Detalhamento de Rotas Otimizadas</div>
            <div className="card-sub">Extrato analítico de todas as rotas processadas no período</div>
          </div>
        </div>

        <div className="tbl-wrap" style={{ marginTop: 12 }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="data">
              <thead>
                <tr>
                  <th>Rota & Data</th>
                  <th>Veículo / Placa</th>
                  <th>Motorista</th>
                  <th>CD de Origem</th>
                  <th style={{ textAlign: 'right' }}>Paradas & Carga</th>
                  <th style={{ textAlign: 'right' }}>Distância</th>
                  <th style={{ textAlign: 'right' }}>Duração</th>
                  <th style={{ textAlign: 'right' }}>Economia</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Ação</th>
                </tr>
              </thead>
              <tbody>
                {filteredRoutes.map((r) => {
                  const rWeight = (r.stops || []).reduce((acc, s) => acc + (s.weightKg || s.deliveryPoint?.weightKg || 0), 0);
                  const distKm = ((r.distanceM || 0) / 1000).toFixed(1);
                  const durFormatted = fmtMin(r.durationS || 0);

                  return (
                    <tr key={r.id}>
                      <td>
                        <div style={{ fontWeight: 600, color: 'var(--text-1)' }}>{r.name}</div>
                        <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{formatDate(r.date || r.createdAt)}</div>
                      </td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{r.vehicle?.name || '—'}</div>
                        <div className="mono" style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
                          {r.vehicle?.plate || '—'}
                        </div>
                      </td>
                      <td style={{ color: 'var(--text-2)' }}>
                        {r.vehicle?.driverName || '—'}
                      </td>
                      <td>
                        <span className="badge b-gray" style={{ fontSize: 11 }}>
                          {r.depot?.name || 'CD Central'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: 600 }}>{r.stops?.length || 0} paradas</div>
                        <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{rWeight.toFixed(0)} kg</div>
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>
                        {distKm} km
                      </td>
                      <td style={{ textAlign: 'right', color: 'var(--text-2)' }}>
                        {durFormatted}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <span
                          className="badge b-teal"
                          style={{
                            fontSize: 11,
                            padding: '2px 8px',
                            fontWeight: 700
                          }}
                        >
                          -{r.savingsPercent ? r.savingsPercent.toFixed(1) : '15.0'}%
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${STATUS_BADGES[r.status] || 'b-gray'}`}>
                          <span className="dot" />
                          {STATUS_LABELS[r.status] || r.status}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => navigate(`/rotas/${r.id}`)}
                          title="Ver itinerário e mapa da rota"
                        >
                          Ver Detalhes
                        </button>
                      </td>
                    </tr>
                  );
                })}

                {filteredRoutes.length === 0 && !loading && (
                  <tr>
                    <td colSpan={10} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-3)' }}>
                      <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Nenhuma rota encontrada</div>
                      <div style={{ fontSize: 13 }}>Tente ajustar os filtros de período, CD, veículo ou termo de busca.</div>
                      <button className="btn btn-secondary btn-sm" onClick={clearFilters} style={{ marginTop: 12 }}>
                        Limpar todos os filtros
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>

              {/* Linha de Totais no Rodapé */}
              {filteredRoutes.length > 0 && (
                <tfoot>
                  <tr className="table-footer-total">
                    <td colSpan={4}>
                      Totais Consolidados ({filteredRoutes.length} rotas filtradas)
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {totalStopsCount} paradas · {totalWeightKg.toFixed(0)} kg
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {fmtKm(totalDistanceKm)} km
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {fmtMin(totalDurationSec)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <span className="badge b-teal" style={{ fontWeight: 700 }}>
                        -{avgSavingsPct.toFixed(1)}% méd.
                      </span>
                    </td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
